/**
 * デザインタブ
 * - 選択中の要素のフォント・色・サイズ・画像差替
 * - 何も選択されていなければスライド全体（背景再生成リンク等）
 * - AI 最適化ボタン
 */
import { AD_FONT_FAMILIES, findAdVectorShape, type AdElement, type AdImagePlaceholderElement, type AdImageSlide, type AdPathElement, type AdTextElement, type AdVectorElement } from '@goenhub/shared';
import { FlipHorizontal, FlipVertical, Loader2, RotateCcw, RotateCw, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useOptimizeAdImageBackground } from '@/hooks/useAdImages';
import { useCustomFonts } from '@/hooks/useCustomFonts';

import { AdImagePickerDialog } from './AdImagePickerDialog';
import { BackgroundOptimizeCompareDialog } from './BackgroundOptimizeCompareDialog';
import { BACKGROUND_LAYER_ID } from './InteractiveCanvas';

interface Props {
  slide: AdImageSlide | null;
  selectedElement: AdElement | null;
  /** Phase 28: 背景レイヤーが選択中か */
  selectedBackgroundLayer?: boolean;
  /** Phase 33.3: 背景 AI 最適化用 */
  adImageId?: string;
  onPatchElement: (patch: Partial<AdElement>) => void;
  onPatchSlide: (patch: Partial<AdImageSlide>) => void;
  /** Phase 28: 背景の位置・サイズ・fit・opacity パッチ */
  onPatchBackground?: (patch: { x?: number; y?: number; w?: number; h?: number; fit?: 'cover' | 'contain' | 'fill'; opacity?: number }) => void;
  onOptimize: () => void;
  isOptimizing: boolean;
}

const FONT_WEIGHTS = [300, 400, 500, 700, 900];

export function DesignTab({
  slide,
  selectedElement,
  selectedBackgroundLayer,
  adImageId,
  onPatchElement,
  onPatchSlide,
  onPatchBackground,
  onOptimize,
  isOptimizing,
}: Props) {
  if (!slide) {
    return (
      <p className="text-xs text-[var(--text-muted)] py-4">
        まず「生成タイプ」で設定して画像を生成してください。
      </p>
    );
  }

  const headerLabel = selectedBackgroundLayer
    ? '背景画像レイヤー'
    : selectedElement
    ? '選択中の要素'
    : 'スライド全体';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          {headerLabel}
        </h4>
        <Button size="sm" variant="ghost" onClick={onOptimize} disabled={isOptimizing}>
          {isOptimizing ? (
            <Loader2 className="size-3 mr-1 animate-spin" />
          ) : (
            <Sparkles className="size-3 mr-1" />
          )}
          AI 最適化
        </Button>
      </div>

      {selectedBackgroundLayer && slide.background ? (
        <BackgroundLayerEditor
          slide={slide}
          adImageId={adImageId}
          onPatchBackground={onPatchBackground}
          onPatchSlide={onPatchSlide}
        />
      ) : selectedElement ? (
        selectedElement.kind === 'text' ? (
          <TextElementEditor element={selectedElement} onPatch={onPatchElement} />
        ) : selectedElement.kind === 'vector' ? (
          <VectorElementEditor element={selectedElement} onPatch={onPatchElement} />
        ) : selectedElement.kind === 'image_placeholder' ? (
          <ImageElementEditor element={selectedElement} onPatch={onPatchElement} />
        ) : (
          <PathElementEditor element={selectedElement} onPatch={onPatchElement} />
        )
      ) : (
        <SlideEditor slide={slide} onPatch={onPatchSlide} />
      )}
    </div>
  );
}

function BackgroundLayerEditor({
  slide,
  adImageId,
  onPatchBackground,
  onPatchSlide,
}: {
  slide: AdImageSlide;
  /** Phase 33.3: 背景 AI 最適化用 */
  adImageId?: string;
  onPatchBackground?: (patch: { x?: number; y?: number; w?: number; h?: number; fit?: 'cover' | 'contain' | 'fill'; opacity?: number }) => void;
  onPatchSlide: (patch: Partial<AdImageSlide>) => void;
}) {
  const bg = slide.background!;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareUrls, setCompareUrls] = useState<{ current: string; next: string } | null>(null);
  const optimizeBg = useOptimizeAdImageBackground();
  const handleOptimize = (
    mode: 'prompt_only' | 'with_reference' | 'upscale_only' | 'with_reference_custom',
  ) => {
    if (!adImageId) {
      toast.error('AdImage ID が不明です');
      return;
    }
    if (mode === 'with_reference_custom' && !customPrompt.trim()) {
      toast.error('修正点を入力してください');
      return;
    }
    optimizeBg.mutate(
      {
        adImageId,
        slideId: slide.id,
        mode,
        customPrompt: mode === 'with_reference_custom' ? customPrompt.trim() : undefined,
      },
      {
        onSuccess: (res) => {
          setCompareUrls({ current: res.currentUrl, next: res.newUrl });
          setCompareOpen(true);
        },
        onError: (e) =>
          toast.error(`最適化失敗: ${e instanceof Error ? e.message : String(e)}`),
      },
    );
  };
  const handleChooseFromCompare = (choice: 'current' | 'new') => {
    if (choice === 'new' && compareUrls) {
      onPatchSlide({
        background: {
          ...bg,
          url: compareUrls.next,
          x: undefined,
          y: undefined,
          w: undefined,
          h: undefined,
        },
      });
      toast.success('新しい画像を採用しました');
    }
    setCompareOpen(false);
  };
  return (
    <div className="space-y-3">
      <Field label="背景画像">
        <div className="relative group">
          <img
            src={bg.url}
            alt="background"
            className="w-full max-h-40 object-contain border border-[var(--border)] rounded-sm bg-[var(--bg-elevated)]"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setPickerOpen(true)}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            画像を変更
          </Button>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setPickerOpen(true)}
          className="w-full mt-2"
        >
          画像選択モーダルを開く
        </Button>
      </Field>
      <AdImagePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(url) => {
          onPatchSlide({ background: { ...bg, url } });
          setPickerOpen(false);
        }}
      />
      <Field label="フィット">
        <div className="flex border border-[var(--border)] rounded-sm overflow-hidden">
          {(['cover', 'contain', 'fill'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => onPatchBackground?.({ fit: f })}
              className={`flex-1 py-2 text-xs ${
                (bg.fit ?? 'cover') === f
                  ? 'bg-[var(--accent)]/10 text-[var(--text-primary)]'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </Field>
      <Field label="不透明度">
        <Input
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={bg.opacity ?? 1}
          onChange={(e) => onPatchBackground?.({ opacity: Number(e.target.value) || 0 })}
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="X">
          <Input type="number" value={bg.x ?? 0} onChange={(e) => onPatchBackground?.({ x: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Y">
          <Input type="number" value={bg.y ?? 0} onChange={(e) => onPatchBackground?.({ y: Number(e.target.value) || 0 })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="幅">
          <Input type="number" value={bg.w ?? 0} onChange={(e) => onPatchBackground?.({ w: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="高さ">
          <Input type="number" value={bg.h ?? 0} onChange={(e) => onPatchBackground?.({ h: Number(e.target.value) || 0 })} />
        </Field>
      </div>
      <Field label="背景プロンプト（編集→再生成時に反映）">
        <Textarea
          rows={4}
          value={bg.prompt}
          onChange={(e) =>
            onPatchSlide({
              background: { ...bg, prompt: e.target.value },
            })
          }
        />
      </Field>

      {/* Phase 33.3: AI 最適化（3 モード） */}
      <div className="space-y-2 pt-2 border-t border-[var(--border)]">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          AI 最適化
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => handleOptimize('prompt_only')}
          disabled={optimizeBg.isPending || !adImageId}
          className="w-full justify-start"
        >
          {optimizeBg.isPending ? (
            <Loader2 className="size-3 mr-2 animate-spin" />
          ) : (
            <Sparkles className="size-3 mr-2" />
          )}
          プロンプトのみで再生成
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => handleOptimize('with_reference')}
          disabled={optimizeBg.isPending || !adImageId}
          className="w-full justify-start"
        >
          {optimizeBg.isPending ? (
            <Loader2 className="size-3 mr-2 animate-spin" />
          ) : (
            <Sparkles className="size-3 mr-2" />
          )}
          現在の画像 + プロンプトで再生成
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => handleOptimize('upscale_only')}
          disabled={optimizeBg.isPending || !adImageId}
          className="w-full justify-start"
        >
          {optimizeBg.isPending ? (
            <Loader2 className="size-3 mr-2 animate-spin" />
          ) : (
            <Sparkles className="size-3 mr-2" />
          )}
          高画質化のみ
        </Button>

        {/* Phase 34.2: 現在の画像 + 手動プロンプト */}
        <div className="space-y-1 pt-1">
          <Textarea
            rows={2}
            placeholder="修正点を日本語で入力（例: 明るくして、左上に余白を作って）"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            className="text-xs"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => handleOptimize('with_reference_custom')}
            disabled={optimizeBg.isPending || !adImageId || !customPrompt.trim()}
            className="w-full justify-start"
          >
            {optimizeBg.isPending ? (
              <Loader2 className="size-3 mr-2 animate-spin" />
            ) : (
              <Sparkles className="size-3 mr-2" />
            )}
            現在の画像 + 修正点で再生成
          </Button>
        </div>

        <p className="text-[10px] text-[var(--text-muted)] leading-tight">
          生成後の比較モーダルで採用するかどうか選べます。両方の画像は Storage に保存されます。
        </p>
      </div>

      <BackgroundOptimizeCompareDialog
        open={compareOpen}
        currentUrl={compareUrls?.current ?? null}
        newUrl={compareUrls?.next ?? null}
        onChoose={handleChooseFromCompare}
        onClose={() => setCompareOpen(false)}
      />
    </div>
  );
}
// BACKGROUND_LAYER_ID は再エクスポート用のために import している
void BACKGROUND_LAYER_ID;

function TextElementEditor({
  element,
  onPatch,
}: {
  element: AdTextElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  const { data: customFonts = [] } = useCustomFonts();
  return (
    <div className="space-y-3">
      <Field label="テキスト">
        <Textarea
          rows={3}
          value={element.content}
          onChange={(e) => onPatch({ content: e.target.value })}
        />
      </Field>
      <Field label="フォント">
        <select
          className="w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 py-2 text-[13px]"
          value={element.fontFamily}
          onChange={(e) => onPatch({ fontFamily: e.target.value })}
        >
          <optgroup label="標準フォント">
            {AD_FONT_FAMILIES.map((f) => (
              <option key={f} value={f} style={{ fontFamily: `"${f}", sans-serif` }}>
                {f}
              </option>
            ))}
          </optgroup>
          {customFonts.length > 0 && (
            <optgroup label="カスタムフォント">
              {customFonts.map((f) => (
                <option key={f.id} value={f.family} style={{ fontFamily: `"${f.family}", sans-serif` }}>
                  {f.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="サイズ (px)">
          <Input
            type="number"
            value={element.fontSize}
            onChange={(e) => onPatch({ fontSize: Number(e.target.value) || element.fontSize })}
          />
        </Field>
        <Field label="ウェイト">
          <select
            className="w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 py-2 text-[13px]"
            value={element.fontWeight}
            onChange={(e) => onPatch({ fontWeight: Number(e.target.value) })}
          >
            {FONT_WEIGHTS.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="文字色">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.color}
              onChange={(e) => onPatch({ color: e.target.value })}
              className="size-9 rounded-sm cursor-pointer border border-[var(--border)] bg-transparent"
            />
            <Input
              value={element.color}
              onChange={(e) => onPatch({ color: e.target.value })}
            />
          </div>
        </Field>
        <Field label="整列">
          <div className="flex border border-[var(--border)] rounded-sm overflow-hidden">
            {(['left', 'center', 'right'] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => onPatch({ align: a })}
                className={`flex-1 py-2 text-xs ${
                  element.align === a
                    ? 'bg-[var(--accent)]/10 text-[var(--text-primary)]'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="行間">
          <Input
            type="number"
            step={0.1}
            value={element.lineHeight ?? 1.2}
            onChange={(e) => onPatch({ lineHeight: Number(e.target.value) || 1.2 })}
          />
        </Field>
        <Field label="文字間 (px)">
          <Input
            type="number"
            value={element.letterSpacing ?? 0}
            onChange={(e) => onPatch({ letterSpacing: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="X">
          <Input
            type="number"
            value={element.x}
            onChange={(e) => onPatch({ x: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Y">
          <Input
            type="number"
            value={element.y}
            onChange={(e) => onPatch({ y: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="幅">
          <Input
            type="number"
            value={element.w}
            onChange={(e) => onPatch({ w: Number(e.target.value) || element.w })}
          />
        </Field>
        <Field label="高さ">
          <Input
            type="number"
            value={element.h}
            onChange={(e) => onPatch({ h: Number(e.target.value) || element.h })}
          />
        </Field>
      </div>
      <TransformControls element={element} onPatch={onPatch} />
    </div>
  );
}

function ImageElementEditor({
  element,
  onPatch,
}: {
  element: AdImagePlaceholderElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-3">
      <Field label="ラベル">
        <Input
          value={element.label}
          onChange={(e) => onPatch({ label: e.target.value })}
        />
      </Field>
      <Field label="画像">
        {element.imageUrl ? (
          <div className="space-y-2">
            <img
              src={element.imageUrl}
              alt={element.label}
              className="w-full max-h-32 object-contain border border-[var(--border)] rounded-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPickerOpen(true)}>
                差し替え
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onPatch({ imageUrl: null })}>
                削除
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setPickerOpen(true)}>
            画像を選択
          </Button>
        )}
        <AdImagePickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={(url) => onPatch({ imageUrl: url })}
        />
      </Field>
      <Field label="フィット">
        <div className="flex border border-[var(--border)] rounded-sm overflow-hidden">
          {(['cover', 'contain', 'fill'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => onPatch({ fit: f })}
              className={`flex-1 py-2 text-xs ${
                element.fit === f
                  ? 'bg-[var(--accent)]/10 text-[var(--text-primary)]'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </Field>
      <Field label="角丸 (px)">
        <Input
          type="number"
          value={element.borderRadius ?? 0}
          onChange={(e) => onPatch({ borderRadius: Number(e.target.value) || 0 })}
        />
      </Field>
      <TransformControls element={element} onPatch={onPatch} />
    </div>
  );
}

function SlideEditor({
  slide,
  onPatch,
}: {
  slide: AdImageSlide;
  onPatch: (patch: Partial<AdImageSlide>) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-muted)]">
        プレビュー上で要素をクリックすると個別編集できます。
      </p>
      {slide.background && (
        <div className="space-y-2">
          <Field label="背景画像">
            <img
              src={slide.background.url}
              alt="background"
              className="w-full max-h-32 object-contain border border-[var(--border)] rounded-sm"
            />
          </Field>
          <Field label="背景プロンプト（編集→再生成時に反映）">
            <Textarea
              rows={4}
              value={slide.background.prompt}
              onChange={(e) =>
                onPatch({
                  background: slide.background
                    ? { ...slide.background, prompt: e.target.value }
                    : null,
                })
              }
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function VectorElementEditor({
  element,
  onPatch,
}: {
  element: AdVectorElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  const def = findAdVectorShape(element.shape);
  return (
    <div className="space-y-3">
      <Field label="形状">
        <div className="flex items-center gap-3 p-2 bg-[var(--bg-elevated)] rounded-sm border border-[var(--border)]">
          <svg viewBox="0 0 100 100" className="w-10 h-10 shrink-0">
            {def && <path d={def.d} fill="currentColor" className="text-[var(--text-primary)]" />}
          </svg>
          <span className="text-xs">{def?.label ?? element.shape}</span>
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="塗り色">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.fill === 'none' ? '#000000' : element.fill}
              onChange={(e) => onPatch({ fill: e.target.value })}
              className="size-9 rounded-sm cursor-pointer border border-[var(--border)] bg-transparent"
            />
            <Input value={element.fill} onChange={(e) => onPatch({ fill: e.target.value })} />
          </div>
        </Field>
        <Field label="線色">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.stroke === 'none' ? '#000000' : element.stroke}
              onChange={(e) => onPatch({ stroke: e.target.value })}
              className="size-9 rounded-sm cursor-pointer border border-[var(--border)] bg-transparent"
            />
            <Input value={element.stroke} onChange={(e) => onPatch({ stroke: e.target.value })} />
          </div>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="線幅 (px)">
          <Input
            type="number"
            value={element.strokeWidth}
            onChange={(e) => onPatch({ strokeWidth: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="不透明度">
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={element.opacity ?? 1}
            onChange={(e) => onPatch({ opacity: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="X">
          <Input type="number" value={element.x} onChange={(e) => onPatch({ x: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Y">
          <Input type="number" value={element.y} onChange={(e) => onPatch({ y: Number(e.target.value) || 0 })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="幅">
          <Input type="number" value={element.w} onChange={(e) => onPatch({ w: Number(e.target.value) || element.w })} />
        </Field>
        <Field label="高さ">
          <Input type="number" value={element.h} onChange={(e) => onPatch({ h: Number(e.target.value) || element.h })} />
        </Field>
      </div>
      <TransformControls element={element} onPatch={onPatch} />
    </div>
  );
}

/**
 * Phase 32.5: ペンツールで描画したパス要素のエディタ
 * - 塗り色 / 線色 / 線幅 / 不透明度 / closed トグル
 * - 頂点リストはプレビュー上のハンドルで編集（ここでは個数のみ表示）
 */
function PathElementEditor({
  element,
  onPatch,
}: {
  element: AdPathElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-muted)]">
        頂点数: {element.points.length}（プレビューの □ をドラッグで頂点移動。ダブルクリックでベジェハンドル生成）
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="塗り">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.fill === 'none' ? '#000000' : element.fill}
              onChange={(e) => onPatch({ fill: e.target.value })}
              className="size-9 rounded-sm cursor-pointer border border-[var(--border)] bg-transparent"
            />
            <Input value={element.fill} onChange={(e) => onPatch({ fill: e.target.value })} />
          </div>
        </Field>
        <Field label="線">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.stroke === 'none' ? '#000000' : element.stroke}
              onChange={(e) => onPatch({ stroke: e.target.value })}
              className="size-9 rounded-sm cursor-pointer border border-[var(--border)] bg-transparent"
            />
            <Input value={element.stroke} onChange={(e) => onPatch({ stroke: e.target.value })} />
          </div>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="線幅 (px)">
          <Input
            type="number"
            min={0}
            value={element.strokeWidth}
            onChange={(e) => onPatch({ strokeWidth: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="不透明度">
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={element.opacity ?? 1}
            onChange={(e) => onPatch({ opacity: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <Field label="閉じたパス">
        <button
          type="button"
          onClick={() => onPatch({ closed: !element.closed } as Partial<AdElement>)}
          className={`w-full py-2 text-xs border rounded-sm ${
            element.closed
              ? 'border-[var(--accent)] bg-[var(--accent)]/10'
              : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          {element.closed ? '閉じる: ON' : '閉じる: OFF'}
        </button>
      </Field>
      <TransformControls element={element} onPatch={onPatch} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-[var(--text-muted)] block mb-1">{label}</label>
      {children}
    </div>
  );
}

/**
 * Phase 31: 全エレメント共通の回転・反転コントロール
 * - 角度入力 (0-360)
 * - 左右 90° 回転ボタン
 * - 左右反転 / 上下反転トグル
 */
function TransformControls({
  element,
  onPatch,
}: {
  element: AdElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  const rotation = element.rotation ?? 0;
  const normalize = (v: number) => ((v % 360) + 360) % 360;
  return (
    <div className="space-y-2 pt-2 border-t border-[var(--border)]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
        変形
      </div>
      <Field label="回転 (0–360°)">
        <div className="flex gap-2 items-center">
          <Input
            type="number"
            min={0}
            max={360}
            value={Math.round(rotation)}
            onChange={(e) => onPatch({ rotation: normalize(Number(e.target.value) || 0) })}
          />
          <input
            type="range"
            min={0}
            max={360}
            value={rotation}
            onChange={(e) => onPatch({ rotation: normalize(Number(e.target.value) || 0) })}
            className="flex-1"
          />
        </div>
      </Field>
      <div className="grid grid-cols-4 gap-1">
        <button
          type="button"
          onClick={() => onPatch({ rotation: normalize(rotation - 90) })}
          className="flex items-center justify-center gap-1 py-2 text-xs border border-[var(--border)] rounded-sm hover:bg-[var(--bg-hover)]"
          aria-label="rotate left 90"
          title="左 90°"
        >
          <RotateCcw className="size-3" />
          <span>90°</span>
        </button>
        <button
          type="button"
          onClick={() => onPatch({ rotation: normalize(rotation + 90) })}
          className="flex items-center justify-center gap-1 py-2 text-xs border border-[var(--border)] rounded-sm hover:bg-[var(--bg-hover)]"
          aria-label="rotate right 90"
          title="右 90°"
        >
          <RotateCw className="size-3" />
          <span>90°</span>
        </button>
        <button
          type="button"
          onClick={() => onPatch({ flipX: !element.flipX })}
          className={`flex items-center justify-center gap-1 py-2 text-xs border rounded-sm ${
            element.flipX
              ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
              : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
          }`}
          aria-label="flip horizontal"
          title="左右反転"
        >
          <FlipHorizontal className="size-3" />
        </button>
        <button
          type="button"
          onClick={() => onPatch({ flipY: !element.flipY })}
          className={`flex items-center justify-center gap-1 py-2 text-xs border rounded-sm ${
            element.flipY
              ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
              : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
          }`}
          aria-label="flip vertical"
          title="上下反転"
        >
          <FlipVertical className="size-3" />
        </button>
      </div>
    </div>
  );
}
